import { Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    CouponRedemption,
    CreateCouponRedemptionInput,
    UpdateCouponRedemptionInput,
    DeleteCouponRedemptionInput,
    RedemptionsByCouponQueryVariables,
    RedemptionsByCouponCodeQueryVariables,
    RedemptionsByUserQueryVariables
} from '@app/core/services/api/API.service';

@Injectable({
    providedIn: 'root'
})
export class CouponRedemptionService {
    private amplifyClient = generateClient();

    constructor() {}

    async listCouponRedemptions(): Promise<CouponRedemption[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listCouponRedemptions
                })
            ).data.listCouponRedemptions.items;
            if (data) return data as CouponRedemption[];
        } catch (error) {
            console.error('Error listing coupon redemptions:', error);
            return null;
        }
    }

    async getCouponRedemption(id: string): Promise<CouponRedemption> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.getCouponRedemption,
                    variables: { id }
                })
            ).data.getCouponRedemption;
            if (data) return data as CouponRedemption;
        } catch (error) {
            console.error('Error getting coupon redemption:', error);
            return null;
        }
    }

    async createCouponRedemption(
        input: CreateCouponRedemptionInput
    ): Promise<CouponRedemption> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.createCouponRedemption,
                    variables: {
                        input
                    }
                })
            ).data.createCouponRedemption;
            if (data) return data as CouponRedemption;
        } catch (error) {
            console.error('Error creating coupon redemption:', error);
            return null;
        }
    }

    async updateCouponRedemption(
        input: UpdateCouponRedemptionInput
    ): Promise<CouponRedemption> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.updateCouponRedemption,
                    variables: {
                        input
                    }
                })
            ).data.updateCouponRedemption;
            if (data) return data as CouponRedemption;
        } catch (error) {
            console.error('Error updating coupon redemption:', error);
            return null;
        }
    }

    async deleteCouponRedemption(
        input: DeleteCouponRedemptionInput
    ): Promise<CouponRedemption> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.deleteCouponRedemption,
                    variables: {
                        input
                    }
                })
            ).data.deleteCouponRedemption;
            if (data) return data as CouponRedemption;
        } catch (error) {
            console.error('Error deleting coupon redemption:', error);
            return null;
        }
    }

    async getRedemptionsByCoupon(
        couponId: string,
        variables?: Omit<RedemptionsByCouponQueryVariables, 'couponId'>
    ): Promise<CouponRedemption[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.redemptionsByCoupon,
                    variables: {
                        couponId,
                        ...variables
                    }
                })
            ).data.redemptionsByCoupon.items;
            if (data) return data as CouponRedemption[];
        } catch (error) {
            console.error('Error getting redemptions by coupon:', error);
            return null;
        }
    }

    async getRedemptionsByCouponCode(
        couponCode: string,
        variables?: Omit<RedemptionsByCouponCodeQueryVariables, 'couponCode'>
    ): Promise<CouponRedemption[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.redemptionsByCouponCode,
                    variables: {
                        couponCode,
                        ...variables
                    }
                })
            ).data.redemptionsByCouponCode.items;
            if (data) return data as CouponRedemption[];
        } catch (error) {
            console.error('Error getting redemptions by coupon code:', error);
            return null;
        }
    }

    async getRedemptionsByUser(
        userId: string,
        variables?: Omit<RedemptionsByUserQueryVariables, 'userId'>
    ): Promise<CouponRedemption[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.redemptionsByUser,
                    variables: {
                        userId,
                        ...variables
                    }
                })
            ).data.redemptionsByUser.items;
            if (data) return data as CouponRedemption[];
        } catch (error) {
            console.error('Error getting redemptions by user:', error);
            return null;
        }
    }
}
